// src/components/dashboard/AccountsReceivableReport.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from "@mui/material";

// Guaranteed Core MUI Icons
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const AccountsReceivableReport = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Filter States
  const [quickRange, setQuickRange] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [program, setProgram] = useState("All");
  const [sortBy, setSortBy] = useState("Client");

  // Pagination & Dataset
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [receivableRows] = useState([
    { id: 1, clientName: "df fdfdd", dateInvoiced: "", payer: "Blue Cross Blue Shield", clientAmount: 0, payerAmount: 0, total: 0 },
    { id: 2, clientName: "Haq Inzimam", dateInvoiced: "", payer: "122", clientAmount: 0, payerAmount: 0, total: 0 },
    { id: 3, clientName: "Haq Inzimam", dateInvoiced: "", payer: "Refreshing", clientAmount: 0, payerAmount: 2220, total: 2220 },
    { id: 4, clientName: "Haq Inzimam", dateInvoiced: "", payer: "Refreshing", clientAmount: 0, payerAmount: 2220, total: 2220 },
    { id: 5, clientName: "Haq Inzimam", dateInvoiced: "", payer: "Refreshing", clientAmount: 0, payerAmount: 2220, total: 2220 },
    { id: 6, clientName: "Haq Inzimam", dateInvoiced: "", payer: "Refreshing", clientAmount: 0, payerAmount: 2220, total: 2220 },
    { id: 7, clientName: "Jamal kamal", dateInvoiced: "", payer: "Ali Ammar", clientAmount: 0, payerAmount: 2, total: 2 },
    { id: 8, clientName: "Patient Test", dateInvoiced: "", payer: "Refreshing", clientAmount: 0, payerAmount: 6, total: 6 },
  ]);

  // Clinical Palette Tokens
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryText = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryText = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setQuickRange("");
    setDateFrom("");
    setDateTo("");
    setProgram("All");
    setSortBy("Client");
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryText,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg },
          "&.Mui-selected": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg, color: tealButton, fontWeight: 700 },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <RequestQuoteRoundedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, color: primaryText }}>
              Accounts Receivable Report
            </Typography>
            <Chip
              label="Financials"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 500, mt: 0.3 }}>
            Track and manage outstanding receivables
          </Typography>
        </Box>
      </Box>

      {/* 2. Filter Reports Section */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SearchIcon sx={{ color: labelTeal, fontSize: "1.3rem" }} />
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryText }}>
            Filter Reports
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          {/* Quick Range */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Quick Range</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={quickRange}
              onChange={(e) => setQuickRange(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select date range...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="This Week">This Week</MenuItem>
              <MenuItem value="This Month">This Month</MenuItem>
            </Select>
          </Box>

          {/* Date of Session */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Date of Session:</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
              <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 600 }}>to</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
            </Box>
          </Box>

          {/* Program */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Program</Typography>
            <Box sx={{ position: "relative" }}>
              <Select
                fullWidth
                size="small"
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
                endAdornment={
                  program !== "All" && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setProgram("All"); }} sx={{ mr: 2, color: secondaryText, p: 0.3 }}>
                      <CloseRoundedIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="ARMHS">ARMHS</MenuItem>
                <MenuItem value="Doula Services">Doula Services</MenuItem>
                <MenuItem value="RN Services">RN Services</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* Sort By */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Sort By:</Typography>
            <Select
              fullWidth
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="Client">Client</MenuItem>
              <MenuItem value="Payer">Payer</MenuItem>
              <MenuItem value="Total">Total</MenuItem>
              <MenuItem value="Date">Date</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* Buttons: Search & Clear */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3.5 }}>
          <Button
            variant="contained"
            startIcon={<SearchIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { backgroundColor: tealButton, boxShadow: "none" },
            }}
          >
            Search
          </Button>

          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              borderColor: tealButton,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 2.8,
              textTransform: "none",
              "&:hover": { borderColor: tealMain, backgroundColor: "rgba(36, 94, 101, 0.05)" },
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. Results Table */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: "16px", border: `1px solid ${softCardBorder}`, overflow: "hidden" }}>
        <TableContainer sx={{ minHeight: "360px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { color: primaryText, fontWeight: 800, fontSize: "0.86rem", backgroundColor: isDark ? "#162238" : "#F8FAFC", py: 2, px: 3 } }}>
                <TableCell>Client Name</TableCell>
                <TableCell>Date Invoiced</TableCell>
                <TableCell>Payer</TableCell>
                <TableCell>Client Amount</TableCell>
                <TableCell>Payer Amount</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receivableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow key={row.id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.03)" } }}>
                  <TableCell sx={{ color: primaryText, fontWeight: 600, fontSize: "0.85rem", py: 1.8, px: 3 }}>{row.clientName}</TableCell>
                  <TableCell sx={{ color: secondaryText, fontSize: "0.85rem", py: 1.8, px: 3 }}>{row.dateInvoiced}</TableCell>
                  <TableCell sx={{ color: primaryText, fontSize: "0.85rem", py: 1.8, px: 3 }}>{row.payer}</TableCell>
                  <TableCell sx={{ color: primaryText, fontSize: "0.85rem", py: 1.8, px: 3 }}>{row.clientAmount}</TableCell>
                  <TableCell sx={{ color: primaryText, fontSize: "0.85rem", py: 1.8, px: 3 }}>{row.payerAmount}</TableCell>
                  <TableCell sx={{ color: primaryText, fontWeight: 700, fontSize: "0.85rem", py: 1.8, px: 3 }}>{row.total}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={receivableRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: `1px solid ${softCardBorder}`, color: secondaryText }}
        />
      </Box>
    </Box>
  );
};

export default AccountsReceivableReport;